import { queryOptions } from '@tanstack/react-query'
import { HTTPError, type Options } from 'ky'
import qs from 'query-string'

import { API_URL } from '@/libs/configs/constant'

import { kyApi } from './ky'

type FactoryType<TParams = object, TBody = any, TResult = any> = {
	params: TParams
	body: TBody
	result: TResult
}

/**
 * Type helpers for inferring input and output types from createApi
 */
// type InferInput<T> = T extends { _input: infer I } ? I : never
// type InferOutput<T> = T extends { _output: infer O } ? O : never

function createApiQuery<
	T extends Partial<FactoryType> = FactoryType,
	TParams = T['params'],
>(url: string) {
	const fn = async (params?: TParams) => {
		const searchParams = params ? qs.stringify(params) : ''
		const res = await apiCall<T['result']>(url, { searchParams, method: 'GET' })
		return res.data
	}

	return {
		key: url,
		api: (params?: TParams) => fn(params),
		qry: (params?: TParams) => {
			const queryKey = params ? [url, params] : [url]
			return queryOptions({
				queryKey,
				queryFn: () => fn(params),
			})
		},
		_input: undefined! as Pick<T, 'params'>,
		_output: undefined! as TOk<T['result']>,
	}
}

function createApiMutation<T extends Partial<FactoryType> = FactoryType>(
	url: string,
	options: Options
) {
	type TArgs = { body: T['body']; params?: T['params'] }

	const fn = async (args?: TArgs) => {
		const json = args ? args.body : undefined
		const res = await apiCall<T['result']>(url, {
			json,
			...options,
		})
		return res.data
	}

	return {
		key: url,
		api: (args?: TArgs) => fn(args),
		_input: undefined! as Pick<T, 'params'>,
		_output: undefined! as TOk<T['result']>,
	}
}

async function apiCall<TResult = unknown>(url: string, options: Options) {
	try {
		const result = await kyApi(`${API_URL}${url || ''}`, options).json<
			TOk<TResult>
		>()
		return result
	} catch (error) {
		/**
		 * Handle HTTP errors
		 * if the response is JSON, parse it and throw an ApiError
		 */
		if (
			error instanceof HTTPError &&
			error.response.headers.get('content-type')?.includes('application/json')
		) {
			const data = await error.response.json<TErr<any>>()
			throw new ApiError(error, data)
		}

		throw error
	}
}

class ApiError extends Error {
	statusCode: number
	code: string | number

	constructor(
		public raw: HTTPError,
		private errData: TErr
	) {
		super(raw.message)
		this.statusCode = raw.response.status
		this.code = errData.code
	}

	getData<TData = unknown>() {
		return this.errData as TErr<TData>
	}

	static is(error: unknown): error is ApiError {
		return error instanceof ApiError
	}
}

export { createApiQuery, createApiMutation, ApiError }
